import { ProgramSharedStep, ProgramWeekEnum } from 'src/modules/programs/models/programs';
import familyIcon from 'src/public/img/family-image-connect@3x.png';
import valuesIcon from 'src/public/img/values.png';

import { CPProgramStepProps } from '../../models/cpProgram';
import { CPProgramSharedStep } from '../../weekShared/models/weekShared';
import { MindfulnessTipsWeekTwoStep } from '../stepComponents/MindfulnessTipsWeekTwoStep';
import { ThinkingMoreAboutValuesStep } from '../stepComponents/ThinkingMoreAboutValuesStep';
import { UnderstandingYourValuesStep } from '../stepComponents/UnderstandingYourValuesStep';
import { ValuesAreasRatingStep } from '../stepComponents/ValuesAreasRatingStep';
import { ValuesAssessmentStep } from '../stepComponents/ValuesAssessmentStep';
import { WeekTwoPlanStep } from '../stepComponents/WeekTwoPlanStep';
import { WhatAreValuesStep } from '../stepComponents/WhatAreValuesStep';
import { CPProgramWeekTwoStep } from './weekTwo';

const steps: (ProgramSharedStep | CPProgramSharedStep | CPProgramWeekTwoStep)[] = [
    ProgramSharedStep.WellbeingCheckInStep,
    ProgramSharedStep.WellbeingScoreStep,
    ProgramSharedStep.FeedbackStep,
    CPProgramSharedStep.MindfulnessTipsStep,
    CPProgramWeekTwoStep.WhatAreValuesStep,
    CPProgramWeekTwoStep.UnderstandingYourValuesStep,
    CPProgramWeekTwoStep.ValuesAssessmentStep,
    CPProgramWeekTwoStep.ValuesAreasRatingStep,
    CPProgramWeekTwoStep.ThinkingMoreAboutValuesStep,
    ProgramSharedStep.PlanStep,
    ProgramSharedStep.CongratulationsStep,
];

const stepsProps: CPProgramStepProps[] = [
    {
        id: CPProgramSharedStep.MindfulnessTipsStep,
        component: MindfulnessTipsWeekTwoStep,
        tooltip: 'Mindfulness Tips',
        flexGrow: false,
    },
    {
        id: CPProgramWeekTwoStep.WhatAreValuesStep,
        component: WhatAreValuesStep,
        tooltip: 'What are Values?',
    },
    {
        id: CPProgramWeekTwoStep.UnderstandingYourValuesStep,
        component: UnderstandingYourValuesStep,
        tooltip: 'Understanding Your Values',
        flexGrow: false,
    },
    {
        id: CPProgramWeekTwoStep.ValuesAssessmentStep,
        component: ValuesAssessmentStep,
        tooltip: 'Assessing Your Values',
        noButtons: true,
    },
    {
        id: CPProgramWeekTwoStep.ValuesAreasRatingStep,
        component: ValuesAreasRatingStep,
        tooltip: 'Rate Your Life Areas',
        grayBackground: true,
        noButtons: true,
    },
    {
        id: CPProgramWeekTwoStep.ThinkingMoreAboutValuesStep,
        component: ThinkingMoreAboutValuesStep,
        tooltip: 'Thinking more about values',
        flexGrow: false,
    },
    {
        id: ProgramSharedStep.PlanStep,
        component: WeekTwoPlanStep,
        tooltip: 'Over the next week...',
        noButtons: true,
    },
];

const t = (key: string) => `cp:week-2.data.${key}`;

export const weekTwoData = {
    id: ProgramWeekEnum.WeekTwo,
    title: t('title'),
    congratulationsSneakPeek: t('congratulationsSneakPeek'),
    feedbackPlaceholder: t('feedbackPlaceholder'),
    definition: t('definition'),
    description: t('description'),
    endOfCourseDefinition: t('endOfCourseDefinition'),
    iconUrl: valuesIcon,
    congratulationsIconUrl: familyIcon,
    weekNumber: 1,
    readableWeekNumber: 2,
    greetingModalContent: t('greetingModalContent'),
    steps,
    stepsProps,
};

export const valuesAssessmentDomains = [
    {
        title: 'Relationships',
        questions: [
            '1. What sort of brother/sister, son/daughter, uncle/aunt, friend, partner, do you want to be?',
            '2. If you were your “ideal” self in your relationships, what would that look like?',
            '3. When do you feel most happy and fulfilled in your relationships?',
        ],
        topics: [
            'acceptance',
            'affection',
            'dependability',
            'flexibility',
            'honesty',
            'humor',
            'independence',
            'love',
            'openness',
            'stability',
            'support',
        ],
        actions: [
            'be affectionate',
            'meet a friend for coffee/dinner',
            'write someone a letter',
            'tell someone you love them',
            'call a friend or family member',
            'other',
        ],
        additionalRate: 1,
    },
    {
        title: 'Play',
        questions: [
            '1. How would you like to spend your free time?',
            '2. What matters to you outside of work?',
            '3. How do you have fun? ',
        ],
        topics: [
            'adventure',
            'challenge',
            'creativity',
            'excitement',
            'fun',
            'relaxation',
            'outdoors',
        ],
        actions: [
            'cook/bake',
            'go for a walk/run',
            'meditate',
            'read a book',
            'watch a movie',
            'other',
        ],
        additionalRate: 0,
    },
    {
        title: 'Work',
        questions: [
            '1. What brings you the most fulfillment in your work?',
            '2. What role do you want your work to have in your life?',
            '3. In what ways do you want to grow at work?',
        ],
        topics: [
            'achievement',
            'autonomy',
            'challenge',
            'creativity',
            'flexibility',
            'meaning',
            'passion',
        ],
        actions: [
            'complete a work task you have been putting off',
            'create an organizational system for work',
            'identify a new skill to develop',
            'sign up for an online class',
            'start a passion project at work',
            'other',
        ],
        additionalRate: 1,
    },
    {
        title: 'Physical Health',
        questions: [
            '1. What role does physical health play in your life?',
            '2. How do you want to look after your health, with regard to sleep, diet, exercise, smoking, alcohol, etc?',
            '3. Why is physical health important to you?',
        ],
        placeholder: 'E.g. Being healthy means I have the energy to spend time with my kids',
        topics: ['acceptance', 'commitment', 'fitness', 'holistic', 'self-control', 'self-esteem'],
        actions: [
            'go for a walk/run',
            'go to a group workout class',
            'meditate',
            'schedule a check-up (dental or medical)',
            'spend 5 minutes stretching when you wake up',
            'other',
        ],
        additionalRate: 0,
    },
    {
        title: 'Community',
        questions: [
            '1. How would you like to contribute to your community or environment?',
            '2. What personal qualities would you like to develop or improve?',
            '3. Does spirituality have a role in your life, if so, what does it look like?',
            '4. How do you relate to people different from you?',
        ],
        placeholder: 'E.g. I want to be a good ally to people that are discriminated against',
        topics: ['acceptance', 'change', 'contribution', 'cooperation', 'duty', 'openness'],
        actions: [
            'attend a religious/spiritual event',
            'engage in a conversation with someone of different beliefs',
            'engage in activism',
            'go to a neighborhood meeting',
            'volunteer',
            'other',
        ],
        additionalRate: 1,
    },
];

const initialValuesAssessmentsDataAreaFields = {
    topics: [],
    additionalInfo: '',
    rate: 0,
    additionalRate: 0,
};

const initialValuesAssessmentsDataAreaDomains = [
    'Relationships',
    'Play',
    'Work',
    'Physical Health',
    'Community',
];

export const initialValuesAssessmentsData = initialValuesAssessmentsDataAreaDomains.map(title => ({
    title,
    ...initialValuesAssessmentsDataAreaFields,
}));
